import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Icon } from '@iconify/react';
import { PAYMENT_METHODS } from './flexpayService';

const StepConfirmation = ({
    txState,
    txMessage,
    txReference,
    txOrderNumber,
    selectedPayment,
    paymentPhone,
    customerInfo,
    cityData,
    deliveryData,
    total,
    onRetry,
}) => {
    return (
        <motion.div
            key="step-2"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="bg-white dark:bg-store-dark border border-slate-200 dark:border-store-border rounded-2xl p-8 text-center space-y-5"
        >
            {/* Processing / Polling */}
            {(txState === 'processing' || txState === 'polling') && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-5"
                >
                    <div className="w-20 h-20 mx-auto relative">
                        <div className="absolute inset-0 rounded-full border-4 border-slate-200 dark:border-store-gray-dark" />
                        <motion.div
                            className="absolute inset-0 rounded-full border-4 border-lime-500 border-t-transparent"
                            animate={{ rotate: 360 }}
                            transition={{
                                duration: 1,
                                repeat: Infinity,
                                ease: 'linear',
                            }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <Icon icon="solar:wallet-bold-duotone" className="text-lime-500 text-2xl" />
                        </div>
                    </div>

                    <h2 className="text-xl font-bold">
                        {txState === 'processing'
                            ? 'Initialisation du paiement...'
                            : 'En attente de confirmation'}
                    </h2>
                    <p className="text-slate-500 dark:text-store-gray text-sm max-w-sm mx-auto">
                        {txState === 'polling'
                            ? selectedPayment === 'mobile-money'
                                ? `Confirmez le paiement sur votre téléphone (${paymentPhone}). N'appuyez pas sur "Retour".`
                                : 'Votre paiement par carte est en cours de traitement...'
                            : txMessage}
                    </p>

                    {txState === 'polling' && (
                        <div className="flex items-center justify-center gap-2 text-xs text-slate-400 dark:text-store-gray">
                            <motion.div
                                className="w-2 h-2 bg-lime-500 rounded-full"
                                animate={{ scale: [1, 1.5, 1] }}
                                transition={{ duration: 1.5, repeat: Infinity }}
                            />
                            {txMessage}
                        </div>
                    )}

                    <p className="text-slate-400 dark:text-store-gray text-[10px] font-mono">
                        Réf: {txReference}
                        {txOrderNumber && ` · Order: ${txOrderNumber}`}
                    </p>
                </motion.div>
            )}

            {/* Success */}
            {txState === 'success' && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                    className="space-y-5"
                >
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                        className="w-20 h-20 bg-lime-500/20 rounded-full flex items-center justify-center mx-auto"
                    >
                        <Icon icon="solar:check-circle-bold" className="text-lime-500 text-4xl" />
                    </motion.div>

                    <h2 className="text-2xl font-bold">Paiement réussi ! 🎉</h2>
                    <p className="text-slate-500 dark:text-store-gray text-sm max-w-sm mx-auto">
                        Votre commande a été confirmée. Vous recevrez un message de
                        confirmation sur <strong>{customerInfo.phone}</strong>.
                    </p>

                    <div className="bg-slate-50 dark:bg-store-gray-dark/50 rounded-xl p-4 space-y-2 text-sm text-left max-w-sm mx-auto">
                        <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-store-gray">N° commande</span>
                            <span className="font-mono font-bold text-lime-600 dark:text-lime-400">
                                {txReference}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-store-gray">Livraison</span>
                            <span className="font-medium">{cityData?.name} — {deliveryData?.delay}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500 dark:text-store-gray">Paiement</span>
                            <span className="font-medium">
                                {PAYMENT_METHODS.find((m) => m.id === selectedPayment)?.label}
                            </span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-slate-200 dark:border-store-border">
                            <span className="font-bold">Total payé</span>
                            <span className="font-bold text-lime-600 dark:text-lime-400">
                                {total.toFixed(2)} $US
                            </span>
                        </div>
                    </div>

                    <Link
                        to="/Boutique"
                        className="inline-flex items-center gap-2 bg-lime-500 hover:bg-lime-400 text-black font-bold px-6 py-3 rounded-xl transition-colors shadow-lg shadow-lime-500/20"
                    >
                        <Icon icon="solar:shop-2-bold" />
                        Retour à la boutique
                    </Link>
                </motion.div>
            )}

            {/* Failed */}
            {txState === 'failed' && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-5"
                >
                    <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
                        <Icon icon="solar:close-circle-bold" className="text-red-500 text-4xl" />
                    </div>

                    <h2 className="text-2xl font-bold">Paiement échoué</h2>
                    <p className="text-slate-500 dark:text-store-gray text-sm max-w-sm mx-auto">
                        {txMessage}
                    </p>

                    {txReference && (
                        <p className="text-slate-400 dark:text-store-gray text-[10px] font-mono">
                            Réf: {txReference}
                        </p>
                    )}

                    <div className="flex gap-3 justify-center">
                        <button
                            onClick={onRetry}
                            className="flex items-center gap-2 bg-slate-200 dark:bg-store-gray-dark hover:bg-slate-300 dark:hover:bg-store-border text-slate-700 dark:text-white font-bold px-6 py-3 rounded-xl transition-colors"
                        >
                            <Icon icon="guidance:right-arrow" />
                            Réessayer
                        </button>
                        <Link
                            to="/Boutique"
                            className="flex items-center gap-2 bg-lime-500 hover:bg-lime-400 text-black font-bold px-6 py-3 rounded-xl transition-colors"
                        >
                            Boutique
                        </Link>
                    </div>
                </motion.div>
            )}
        </motion.div>
    );
};

export default StepConfirmation;
